import { z } from 'zod';
const isoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD');
const shortText = z.string().trim().max(400);
const longText = z.string().trim().max(4000);
export const CLINICAL_FORMS = {
    obstetric_history: {
        version: 1,
        label: 'Obstetric history',
        appliesTo: { sex: 'female', minAgeYears: 10 },
        nurse: false,
        schema: z
            .object({
            gravida: z.number().int().min(0).max(30),
            para: z.number().int().min(0).max(30),
            abortions: z.number().int().min(0).max(30),
            living: z.number().int().min(0).max(30),
            lmp: isoDay.optional(),
            edd: isoDay.optional(),
            cycleDays: z.number().int().min(15).max(90).optional(),
            notes: longText.optional(),
        })
            .strict(),
    },
    menstrual_history: {
        version: 1,
        label: 'Menstrual history',
        appliesTo: { sex: 'female', minAgeYears: 10 },
        nurse: false,
        schema: z
            .object({
            menarcheAgeYears: z.number().int().min(6).max(25).optional(),
            lmp: isoDay.optional(),
            cycleDays: z.number().int().min(15).max(90).optional(),
            durationDays: z.number().int().min(1).max(15).optional(),
            regular: z.boolean().optional(),
            menopause: z.boolean().optional(),
            notes: longText.optional(),
        })
            .strict(),
    },
    paediatric_history: {
        version: 1,
        label: 'Paediatric history',
        appliesTo: { maxAgeYears: 18 },
        nurse: false,
        schema: z
            .object({
            birthWeightKg: z.number().min(0.3).max(7).optional(),
            gestationWeeks: z.number().int().min(20).max(45).optional(),
            delivery: z.enum(['normal', 'caesarean', 'assisted']).optional(),
            feeding: z.enum(['breast', 'formula', 'mixed', 'family_food']).optional(),
            milestones: z.enum(['on_time', 'delayed', 'not_assessed']).optional(),
            notes: longText.optional(),
        })
            .strict(),
    },
    immunisation_status: {
        version: 1,
        label: 'Immunisation status',
        appliesTo: {},
        nurse: true,
        schema: z
            .object({
            doses: z
                .array(z.object({ vaccine: z.string().trim().min(2).max(80), dose: z.string().trim().max(20), date: isoDay.optional(), given: z.boolean() }).strict())
                .min(1)
                .max(40),
            notes: shortText.optional(),
        })
            .strict(),
    },
    growth_measurement: {
        version: 1,
        label: 'Growth measurement',
        appliesTo: { maxAgeYears: 18 },
        nurse: true,
        schema: z
            .object({
            weightKg: z.number().min(0.3).max(200).optional(),
            heightCm: z.number().min(30).max(220).optional(),
            headCircumferenceCm: z.number().min(20).max(70).optional(),
            measuredOn: isoDay.optional(),
        })
            .strict()
            .refine((value) => value.weightKg !== undefined || value.heightCm !== undefined || value.headCircumferenceCm !== undefined, {
            message: 'Record at least one measurement',
        }),
    },
    procedure_note: {
        version: 1,
        label: 'Procedure note',
        appliesTo: {},
        nurse: false,
        schema: z
            .object({
            procedure: z.string().trim().min(2).max(200),
            indication: shortText.optional(),
            anaesthesia: z.enum(['none', 'local', 'sedation', 'regional', 'general']).optional(),
            findings: longText.optional(),
            complications: shortText.optional(),
            performedBy: z.string().trim().max(160).optional(),
        })
            .strict(),
    },
    patient_education: {
        version: 1,
        label: 'Patient education',
        appliesTo: {},
        nurse: true,
        schema: z
            .object({
            topics: z.array(z.string().trim().min(2).max(80)).min(1).max(15),
            method: z.enum(['verbal', 'leaflet', 'demonstration', 'video']),
            understood: z.boolean(),
            notes: shortText.optional(),
        })
            .strict(),
    },
    pre_procedure_consent: {
        version: 1,
        label: 'Pre-procedure consent',
        appliesTo: {},
        nurse: false,
        schema: z
            .object({
            procedure: z.string().trim().min(2).max(200),
            risksExplained: z.string().trim().min(5).max(2000),
            signedBy: z.string().trim().min(2).max(160),
            relation: z.enum(['self', 'parent', 'spouse', 'guardian', 'other']),
            witness: z.string().trim().max(160).optional(),
        })
            .strict(),
    },
};
export function isClinicalFormCode(code) {
    return Object.prototype.hasOwnProperty.call(CLINICAL_FORMS, code);
}
export function formApplies(code, patient) {
    const rule = CLINICAL_FORMS[code].appliesTo;
    if (rule.sex && patient.sex !== rule.sex)
        return false;
    if (rule.minAgeYears !== undefined && patient.ageYears !== null && patient.ageYears < rule.minAgeYears)
        return false;
    if (rule.maxAgeYears !== undefined && (patient.ageYears === null || patient.ageYears >= rule.maxAgeYears))
        return false;
    return true;
}
export function validateClinicalForm(code, data) {
    if (!isClinicalFormCode(code))
        return { ok: false, message: 'That form is not in the catalogue', path: 'formCode' };
    const parsed = CLINICAL_FORMS[code].schema.safeParse(data);
    if (!parsed.success) {
        const issue = parsed.error.issues[0];
        return { ok: false, message: issue?.message ?? 'The form is not complete', path: issue?.path.join('.') };
    }
    return { ok: true, data: parsed.data };
}
function normalCdf(z) {
    const t = 1 / (1 + 0.2316419 * Math.abs(z));
    const d = 0.3989423 * Math.exp((-z * z) / 2);
    const tail = d * t * (0.3193815 + t * (-0.3565638 + t * (1.781478 + t * (-1.821256 + t * 1.330274))));
    return z > 0 ? 1 - tail : tail;
}
export function lmsZScore(value, row) {
    if (row.l === 0)
        return Math.log(value / row.m) / row.s;
    return (Math.pow(value / row.m, row.l) - 1) / (row.l * row.s);
}
export function lmsPercentile(value, row) {
    if (!row || value <= 0)
        return null;
    return Math.round(normalCdf(lmsZScore(value, row)) * 1000) / 10;
}
//# sourceMappingURL=clinical-forms.js.map